import { motion } from "framer-motion";
import { PROJECT, AMENITIES } from "../../data/project";

export default function Overview() {
  return (
    <section
      id="overview"
      data-testid="overview-section"
      className="relative bg-[#F4F2EC] py-24 md:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Editorial intro */}
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-5"
          >
            <div className="overline mb-5">01 / The Estate</div>
            <h2
              className="font-serif leading-[1.02] tracking-tight text-[#1A3626]"
              style={{ fontSize: "clamp(40px, 5vw, 72px)" }}
            >
              An Andalusian
              <br />
              sanctuary on
              <br />
              <span className="italic">the island.</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="col-span-12 md:col-span-6 md:col-start-7"
          >
            <p className="text-lg md:text-xl font-light leading-relaxed text-[#1A3626]/85">
              Hudayriyat Golf Estates is Modon's most considered residential
              address. A master-planned community of mansions, villas and
              townhomes weaving through Abu Dhabi's first championship 18-hole
              course.
            </p>
            <p className="mt-6 text-base font-light leading-relaxed text-[#6A736E]">
              Designed in a contemporary Andalusian vernacular, with warm
              limestone, deep shaded loggias and courtyards open to the sky. The
              estate sits between the fairways and a serene water channel,
              twenty minutes from downtown Abu Dhabi yet entirely apart from
              it. A private island address with the infrastructure of a
              capital city.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-6 pt-8 border-t hairline">
              <Pill label="Island" value="Hudayriyat" />
              <Pill label="Developer" value="Modon" />
              <Pill label="Zone" value="Golf Living" />
            </div>
          </motion.div>
        </div>

        {/* Masterplan aerial — the island in full */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 md:mt-28 overflow-hidden aspect-[16/9] md:aspect-[21/9] group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_investment-showcase-4/artifacts/ysdqjnts_ChatGPT%20Image%20May%2022%2C%202026%2C%2003_31_08%20PM.png"
            alt="Hudayriyat Island masterplan"
            className="w-full h-full object-cover transition-transform [transition-duration:1800ms] ease-out group-hover:scale-[1.05]"
          />
        </motion.div>

        {/* Amenities */}
        <div className="mt-24 md:mt-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="overline mb-4">The Lifestyle</div>
            <h3
              className="font-serif text-[#1A3626] leading-[1.05]"
              style={{ fontSize: "clamp(32px, 3.8vw, 54px)" }}
            >
              Ten worlds within
              <br />
              <span className="italic">one address.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-8 md:pt-2">
            <ul className="grid grid-cols-1 sm:grid-cols-2 divide-y hairline border-t border-b hairline">
              {AMENITIES.map((a, i) => (
                <motion.li
                  key={a.name}
                  data-testid={`amenity-${i}`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="flex items-center justify-between py-5 sm:px-1 sm:[&:nth-child(2n)]:border-l hairline"
                >
                  <span className="font-serif text-xl md:text-2xl text-[#1A3626]">
                    {a.name}
                  </span>
                  <span className="text-[#B3906A] text-xs tracking-[0.2em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pill({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#B3906A] font-semibold">
        {label}
      </div>
      <div className="mt-2 font-serif text-xl text-[#1A3626]">{value}</div>
    </div>
  );
}
